import "../ui/styles/modes.css";
import { Modes, ModeOutcome } from "@highmarch/app";
import { trialEndsAt, trialWeek } from "@highmarch/domain";
import { toast } from "../ui/toast";
import { showIronSheet } from "../ui/modes/ironSheet";
import { showEndlessSheet } from "../ui/modes/endlessSheet";
import { modeEndHtml } from "../ui/modes/modeEnd";
import { fmtLeft } from "../ui/hub/eventsSheet";
import { eventClock } from "./events";
import { EndScreenFacts, Feature, HubApi, MORE_ORDER } from "./registry";

/** The Endless row sits just under Events on the More sheet. */
const ENDLESS_ORDER = MORE_ORDER.events + 2;
/** The mode block sits before the events' block on the end screen (END_ORDER 20 there). */
const END_ORDER = 15;
/** Run outcomes kept for end-screen redraws. */
const OUTCOMES_KEPT = 4;

/**
 * Iron challenges and Endless mode (M21, docs/MODES.md): the IRON button on the battle card and the campaign map (the
 * Iron sheet before the start), More → Endless (bests, the weekly chest, PLAY), the end screen's mode block, a Guide
 * topic each, the away card's weekly-chest line and debug hooks for captures. Rules live in @highmarch/app modes.ts.
 */
export const modesFeature: Feature = {
  id: "modes",
  register(ctx, registry) {
    const modes = new Modes(ctx.catalog);
    const now = eventClock(() => ctx.now(), ctx.debug, typeof location === "undefined" ? "" : location.search);
    const startIron = (level: number) => registry.startRun(level, modes.ironRun(level));
    const startEndless = () => registry.startRun(modes.endlessLevel, modes.endlessRun());
    const openIron = (level: number, parent: HTMLElement) => {
      if (!modes.ironOpen(level)) return;
      showIronSheet(parent, { catalog: ctx.catalog, modes, now, start: startIron, onChange: () => ctx.persist() }, level);
    };
    registry.openIron = openIron;
    const openEndless = (hub: HubApi) => {
      modes.pendingChest(now());
      ctx.persist();
      showEndlessSheet(hub.parent, { catalog: ctx.catalog, modes, now, onChange: () => hub.changed(), start: startEndless });
    };
    const chestWaiting = (): number => (modes.pendingChest(now()) !== null ? 1 : 0);

    // The battle card's IRON button (ui/hub/battleCard.ts draws it): delegated, since the hub re-renders the card.
    registry.hubWidget({
      id: "iron",
      mount: (hub) => {
        const onClick = (e: MouseEvent) => {
          const b = (e.target as HTMLElement | null)?.closest?.<HTMLElement>(".battlecard .ironbtn");
          if (b && Number(b.dataset.level) > 0) openIron(Number(b.dataset.level), hub.parent);
        };
        hub.parent.addEventListener("click", onClick);
        return { dispose: () => hub.parent.removeEventListener("click", onClick) };
      },
    });

    registry.moreRow({
      id: "endless", order: ENDLESS_ORDER, icon: "endless", home: "events",
      label: () => "Endless",
      note: () => {
        if (!modes.endlessOpen) return `Clear level ${modes.endlessUnlockAfter} to unlock`;
        if (chestWaiting() > 0) return "Last week's chest is ready";
        const s = ctx.catalog.modes;
        modes.pendingChest(now());
        return s.weekWaves > 0 ? `This week: wave ${s.weekWaves} · ${fmtLeft(trialEndsAt(trialWeek(now())) - now())} left` : "How long can you hold?";
      },
      dot: () => (modes.endlessOpen ? chestWaiting() : 0),
      visible: () => modes.endlessOpen || ctx.catalog.highestLevelCleared >= modes.endlessUnlockAfter - 5,
      open: (hub) => { if (modes.endlessOpen) openEndless(hub); else toast(hub.parent, `Endless opens after level ${modes.endlessUnlockAfter}`); },
    });

    // The end screen: a run's outcome is settled once and drawn from the cache.
    const outcomes = new Map<string, ModeOutcome>();
    const outcomeFor = (f: EndScreenFacts): ModeOutcome => {
      const key = `${f.level}:${f.sim.seed}:${f.won ? "w" : "l"}${f.sim.stagesCleared}`;
      let o = outcomes.get(key);
      if (!o) {
        o = modes.finishRun({ level: f.level, won: f.won, stagesCleared: f.sim.stagesCleared, points: f.sim.points, modifiers: f.sim.modifiers, key, now: now() });
        outcomes.set(key, o);
        if (outcomes.size > OUTCOMES_KEPT) outcomes.delete(outcomes.keys().next().value!);
        if (o.kind !== null) ctx.persist();
      }
      return o;
    };
    registry.endScreenExtra({ id: "modes", order: END_ORDER, html: (f) => (f.sim.isIron || f.sim.isEndless ? modeEndHtml(outcomeFor(f), f.level, f.won) : "") });

    registry.guideTopic({
      id: "iron", group: "castle", label: "Iron challenges", art: { icon: "iron" }, fromLevel: 2, steps: [],
      lines: [
        "Earn all **3 stars** on a level to open its **Iron** challenge: every wave rolled into **one long wave**, with only **three towers**.",
        "One try a day: a win lights the level's **Iron star** and pays **Marks**. Find **IRON** on the battle card or the map.",
      ],
    });
    registry.guideTopic({
      id: "endless", group: "castle", label: "Endless", art: { icon: "fast" }, fromLevel: modes.endlessUnlockAfter + 1, steps: [],
      lines: [
        "**Endless** waves grow stronger forever, with a boss every five. Your score is the waves you survive.",
        "Your best of the week sets a **weekly chest**, claimed in **Events → Endless** once the week ends.",
      ],
    });

    registry.awayItems(({ awaySeconds }) => {
      if (awaySeconds === null || !modes.endlessOpen || chestWaiting() === 0) return [];
      return [{ icon: "chest", title: "Your Endless chest is ready", detail: "Last week's best · Events → Endless" }];
    });

    if (ctx.debug && typeof window !== "undefined") {
      // Captures and smoke tests: open the sheets, give 3 stars, set this week's best, leave a chest waiting.
      (window as unknown as { __tdModes: unknown }).__tdModes = {
        modes,
        openIron: (level: number) => openIron(level, ctx.ui),
        openEndless: () => showEndlessSheet(ctx.ui, { catalog: ctx.catalog, modes, now, onChange: () => ctx.persist(), start: startEndless }),
        threeStar: (...levels: number[]) => { for (const l of levels) ctx.catalog.recordStars(l, 7); ctx.persist(); },
        best: (waves: number, points = waves * 180) => { modes.pendingChest(now()); ctx.catalog.modes.recordEndless(modes.data, now(), `debug:${Date.now()}`, { waves, points }); ctx.persist(); },
        lastWeek: (waves: number) => { const s = ctx.catalog.modes; s.week = trialWeek(now()) - 1; s.weekWaves = waves; s.weekPoints = waves * 180; modes.pendingChest(now()); ctx.persist(); },
        startIron, startEndless,
      };
    }
  },
};
